import {
  fetchTaskDetail,
  done,
  storage,
  fetchLog,
  fetchSign,
  signStorage,
  signSubmit,
} from "@/api/approval";
function decodeUrlPath(url) {
  return url.split(',').map((part) => {
    const qIndex = part.indexOf('?');
    if (qIndex === -1) return decodeURIComponent(part);
    return decodeURIComponent(part.substring(0, qIndex)) + part.substring(qIndex);
  }).join(',');
}

function decodeExtraUrls(obj) {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj)) {
    obj.forEach(item => decodeExtraUrls(item));
  } else {
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (['completePath', 'url', 'path', 'attachment', 'attachmentPaths', 'fileUrl', 'filePath', 'fileName', 'originalName', 'name'].includes(key)) {
        if (typeof val === 'string' && val.includes('%')) {
          try { obj[key] = decodeUrlPath(val); } catch (e) {}
        }
      } else if (val && typeof val === 'object') {
        decodeExtraUrls(val);
      }
    });
  }
}

function safeParseExtra(str) {
  if (typeof str !== 'string') {
    return str || {};
  }
  try {
    return JSON.parse(str);
  } catch (e) {
    console.warn('extra JSON parse failed, attempting recovery');
    const posMatch = String(e.message).match(/position\s*(\d+)/);
    const pos = posMatch ? parseInt(posMatch[1], 10) : 0;
    if (pos > 0) {
      console.warn('Context around problem:', str.substring(Math.max(0, pos - 50), pos + 50));
    }
    // 手动扫描修复 attachment 字段中未转义的双引号
    let fixed = '';
    let i = 0;
    while (i < str.length) {
      const m = str.substring(i).match(/^("attachment"\s*:\s*")/);
      if (m) {
        fixed += m[0];
        i += m[0].length;
        let value = '';
        let escape = false;
        let braceDepth = 0;
        let started = false;
        while (i < str.length) {
          const ch = str[i];
          if (escape) {
            value += ch;
            escape = false;
          } else if (ch === '\\') {
            value += ch;
            escape = true;
          } else if (ch === '{') {
            value += ch;
            braceDepth++;
            started = true;
          } else if (ch === '}') {
            value += ch;
            braceDepth--;
            if (started && braceDepth === 0 && i + 1 < str.length && str[i + 1] === '"') {
              fixed += value;
              i++;
              fixed += '"'; // closing quote of attachment value
              break;
            }
          } else if (ch === '"') {
            value += '\\"'; // unescaped inner quote → escape it
          } else {
            value += ch;
          }
          i++;
        }
        if (i >= str.length) {
          fixed += value; // didn't find closing quote, append rest
        }
      } else {
        fixed += str[i];
      }
      i++;
    }
    try {
      return JSON.parse(fixed);
    } catch (e2) {
      console.error('extra JSON still invalid after manual fix', e2);
      const posMatch2 = String(e2.message).match(/position\s*(\d+)/);
      if (posMatch2 && parseInt(posMatch2[1], 10) > 0) {
        console.warn('Context of fixed string around problem:', fixed.substring(Math.max(0, parseInt(posMatch2[1], 10) - 50), parseInt(posMatch2[1], 10) + 50));
      }
      // 最后兜底：逐字段正则宽松提取（字符串/数字/布尔），损坏字段丢弃，保证其余字段可展示
      try {
        const obj = {};
        const re = /"([A-Za-z0-9_]+)":("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|true|false|null)/g;
        let m;
        while ((m = re.exec(str)) !== null) {
          let v = m[2];
          try {
            v = JSON.parse(v);
          } catch (e3) {
            // 值本身损坏时保留原文（去两端引号），由展示层兜底
            if (typeof v === 'string' && v.startsWith('"')) {
              v = v.slice(1, -1);
            }
          }
          obj[m[1]] = v;
        }
        if (Object.keys(obj).length > 0) {
          console.warn('extra parsed by lenient field extraction:', Object.keys(obj).join(','));
          return obj;
        }
      } catch (e4) {
        console.warn('lenient field extraction failed', e4);
      }
      return {};
    }
  }
}

export default {
  namespaced: true,

  state () {
    return {
      taskId: null, // 审批任务 id
      instanceId: null, // 审批流 id
      loading: false, // 加载状态
      detail: {}, // 审批任务详情
      history: [], // 历史记录

      oldDelayedDate: null, // 旧的延期字段
      log: {}, // 审批日志

      // 加签
      assignees: [], //加签人
      signType: "", //加签类型
      onset: false, //是否显示选择按钮（设置是否立即结束当前任务，不结束则可查看会签的其他人的审核记录）

      // 校审意见列表（filteredOpinionList）
      filteredOpinionList: [],

      // 工单处理记录（工单处理人节点填写，OaWorkOrder组件输入、Form提交时读取）
      workOrderRecord: {
        problemType: "demand",
        modifyContent: "",
        files: [],
        isCharge: false,
      },
    };
  },

  getters: {
    // 是否是合同审批
    isContract (state) {
      return state.detail.processDefinitionKey === "con_configure";
    },
    // 是否是项目阶段进度审批
    isProgress (state) {
      return state.detail.processDefinitionKey === "prj_phase_progress";
    },
    // 是否是项目阶段产值分配审批
    isAllocate (state) {
      return state.detail.processDefinitionKey === "prj_configure";
    },
    // 任务类型为：合同或项目申请调整
    isAdjust (state) {
      return state.detail.processDefinitionKey === "prj_configure_adjust";
    },

    disabled (state) {
      // 不是待审核就全禁用
      // 不是待重新提交就禁用
      // 待阅已阅的任务没有操作权限
      return (
        (state.detail.status !== "ASSIGNED" &&
          state.detail.status !== "RESUBMITED") ||
        state.detail.taskDefinitionKey === "notify_task"
      ); // 有值就禁用
    },

    // 打分是否渲染，只有阶段进度审批时，并且是区域指挥完成这项审批的时候
    scoreVisible (state) {
      return (
        state.detail.taskDefinitionKey ===
        "prj_progress_y_project_area_director"
      );
    },
    score (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.phaseProgressData
      ) {
        return state.detail.extra.phaseProgressData.score;
      } else {
        return 0;
      }
    },

    // 选择产值分配计算公式
    formulaVisible (state) {
      return state.detail.taskDefinitionKey === "prj_progress_formula";
    },
    // 公式
    formula (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.phaseProgressData
      ) {
        return state.detail.extra.phaseProgressData.formula;
      } else {
        return null;
      }
    },
    // 系数
    factor (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.phaseProgressData
      ) {
        return state.detail.extra.phaseProgressData.factor || 0.12;
      } else {
        return 0.12;
      }
    },

    // 是否是合同分包初审节点（可以编辑四个新字段）
    isContractSubpackageFirstReview (state) {
      return state.detail.taskDefinitionKey === "con_subpackage_first_review" && state.detail.processDefinitionKey === "con_configure";
    },
    // 是否是收入合同审批吉瑞（合同管理员）节点
    isContractRegisterJiruiNode (state) {
      return state.detail.taskDefinitionKey === "con_manager" && state.detail.processDefinitionKey === "con_register";
    },
    // 考核年份
    assessmentYear (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.assessmentYear;
      } else {
        return null;
      }
    },
    // 承接方式
    undertakingMode (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.undertakingMode;
      } else {
        return null;
      }
    },
    // 经营组ID
    businessGroupId (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.businessGroupId;
      } else {
        return null;
      }
    },
    // 经营组名称
    businessGroup (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.businessGroup;
      } else {
        return null;
      }
    },
    // 经营对接人ID
    businessContactUserId (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.businessContactUserId;
      } else {
        return null;
      }
    },
    // 经营对接人名称
    businessContactUserName (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractData
      ) {
        return state.detail.extra.contractData.businessContactUserName;
      } else {
        return null;
      }
    },

    // 是否是督办审批任务
    isSupervise (state) {
      return state.detail.processDefinitionKey === "ofc_supervise";
    },
    // 是否是督办任务的完成者（不是发起者）
    isSuperviseWorker (state) {
      return state.detail.taskDefinitionKey?.startsWith("ofc_supervise_worker");
    },

    // 是否需要财务审批
    needFinancialApproval (state) {
      return state.detail.taskDefinitionKey === "prj_allocate_first_review" && state.detail.processDefinitionKey === 'con_amount_adjust'
    },
    isFinancialApproval (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.contractChangeAmountData
      ) {
        return state.detail.extra.contractChangeAmountData.isFinancialApproval;
      } else {
        return null;
      }
    },

    // 完成情况说明
    executeRemarks (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.superviseTaskData
      ) {
        return state.detail.extra.superviseTaskData.executeRemarks;
      } else {
        return null;
      }
    },
    // 完成情况附件
    executeAttachments (state) {
      if (
        state.detail &&
        state.detail.extra &&
        state.detail.extra.superviseTaskData &&
        Array.isArray(state.detail.extra.superviseTaskData.executeAttachments)
      ) {
        return state.detail.extra.superviseTaskData.executeAttachments;
      } else {
        return [];
      }
    },
    // 是否延期
    isDelayed (state) {
      if (state.detail.extra && state.detail.extra.superviseTaskData) {
        return state.detail.extra.superviseTaskData.isDelayed;
      }
      return 0;
    },

    // 完成情况说明
    delayedDate (state) {
      if (
        state.detail.extra &&
        state.detail.extra.superviseTaskData &&
        state.detail.extra.superviseTaskData.delayedDate
      ) {
        return state.detail.extra.superviseTaskData.delayedDate;
      } else {
        return null;
      }
    },

    delayRemarks (state) {
      if (
        state.detail.extra &&
        state.detail.extra.superviseTaskData &&
        state.detail.extra.superviseTaskData.delayRemarks
      ) {
        return state.detail.extra.superviseTaskData.delayRemarks;
      } else {
        return null;
      }
    },

    // 是否是开票、退票申请
    isInvoice (state) {
      return state.detail.processDefinitionKey === "fin_invoice";
    },

    // 开票申请中的填写票的人
    isFinanceDrawer (state) {
      return state.detail.taskDefinitionKey === "inv_finance_drawer";
    },

    // 这次申请的类型：开票、退票、换票
    applyType (state) {
      return state.detail.extra?.applyType;
    },

    // 用户填完的销方
    salesUnitName (state) {
      return state.detail.extra?.invoiceInfos?.salesUnitName;
    },

    // 开票人
    drawer (state) {
      return state.detail.extra?.invoiceInfos?.drawer;
    },
    // 开票日期
    drawDate (state) {
      return state.detail.extra?.invoiceInfos?.drawDate;
    },

    // 用户已经填完的发票列表
    invoiceList (state) {
      return state.detail.extra?.invoiceInfos?.invoiceList ?? [];
    },

    // 开票退票换票申请中需要填写送达信息的人
    isPostMan (state) {
      return (
        state.detail.taskDefinitionKey === "inv_branch_interface_1" ||
        state.detail.taskDefinitionKey === "inv_applicant"
      );
    },
    sendInfo (state) {
      if (state.detail.extra && state.detail.extra.sendInfo) {
        return state.detail.extra.sendInfo;
      } else {
        return {};
      }
    },
    // 送达附件
    sendAttachment (state) {
      if (state.detail.extra && state.detail.extra.sendInfo) {
        return state.detail.extra.sendInfo.attachment;
      }
    },
    // 开票退票换票申请中需要确认送达信息的人
    isConfirmer (state) {
      return (
        state.detail.taskDefinitionKey === "inv_product_master_1" ||
        state.detail.taskDefinitionKey === "inv_applicant_leader_1"
      );
    },
    // 是否确认送达
    isSent (state) {
      if (state.detail.extra && state.detail.extra.sendInfo) {
        return state.detail.extra.sendInfo.isSent;
      }
    },

    // 是否是档案出借审批
    isArchiveBorrow (state) {
      return state.detail.processDefinitionKey === "arc_borrow";
    },

    // 是否是档案移交审批
    isArchiveTransfer (state) {
      return state.detail.processDefinitionKey === "arc_transfer";
    },

    isMaterial (state) {
      return state.detail.processDefinitionKey === "ofc_inv_apply";
    },
    isEntertain (state) {
      return state.detail.processDefinitionKey === "ofc_entertain_apply";
    },
    isPurchase (state) {
      return state.detail.processDefinitionKey === "oa_purchase_materials";
    },
    isAssets (state) {
      return state.detail.processDefinitionKey === "oa_it_asset_apply";
    },
    isAssetsHandover (state) {
      return state.detail.processDefinitionKey === "oa_it_asset_transfer";
    },
    isAssetsScrapIt (state) {
      return state.detail.processDefinitionKey === "oa_it_asset_scrap";
    },
    isAssetsScrap (state) {
      return state.detail.processDefinitionKey === "oa_asset_scrap";
    },
    isAssetsScrapHandle (state) {
      return state.detail.processDefinitionKey === "oa_asset_scrap_disposal";
    },
    isGift (state) {
      return state.detail.processDefinitionKey === "ofc_gift_apply";
    },
    // 是否是合同登记审批
    isContractRegister (state) {
      return state.detail.processDefinitionKey === "con_register";
    },
    // 是否是合同调整
    isContractAdjust (state) {
      return state.detail.processDefinitionKey === "con_amount_adjust";
    },
    // 是否是分包登记审批
    isSubpackageRegister (state) {
      return state.detail.processDefinitionKey === "subcon_register";
    },
    // 是否是分院分包登记审批
    isBranchSubpackageRegister (state) {
      return state.detail.processDefinitionKey === "subcon_branch_register";
    },
    // 是否是框架类合同登记审批
    isFrameworkContractRegister (state) {
      return state.detail.processDefinitionKey === "con_kj_register";
    },

    // 是否是总承包项目组织机构建立
    isEpcOrganize (state) {
      return state.detail.processDefinitionKey === "epc_project";
    },
    // 是否是投标控制预算审核意见表
    isBudgetComment (state) {
      return state.detail.processDefinitionKey === "epc_budget_review";
    },
    // 是否投标控制预算审批表
    isEpcBidbudget (state) {
      return state.detail.processDefinitionKey === "epc_budget";
    },

    // 是否是管理目标责任书
    isManageObject (state) {
      return state.detail.processDefinitionKey === "epc_manage_objective";
    },
    // 是否是管理计划
    isManagePlan (state) {
      return state.detail.processDefinitionKey === "epc_manage_plan";
    },
    // 是否是管理计划变更
    isManagePlanChange (state) {
      return state.detail.processDefinitionKey === "epc_manage_plan_update";
    },

    // 是否是施工进度计划
    isConstructionSchedule (state) {
      return state.detail.processDefinitionKey === "epc_construct_progress";
    },
    // 是否是施工进度计划
    isConstructionScheduleChange (state) {
      return (
        state.detail.processDefinitionKey === "epc_construct_progress_update"
      );
    },
    // 是否是实施控制预算
    isConstructionBudget (state) {
      return state.detail.processDefinitionKey === "epc_implementation_budget";
    },
    // 是否是策划 实施控制预算审核
    isPlanBudgetComment (state) {
      return (
        state.detail.processDefinitionKey === "epc_implementation_budget_review"
      );
    },
    // 是否是设计成果确认
    isResultConfirm (state) {
      return state.detail.processDefinitionKey === "epc_result_confirm";
    },
    // 是否是设计变更审批-符合审计原则
    isChangeReview (state) {
      return state.detail.processDefinitionKey === "epc_change_review";
    },
    // 是否是设计变更审批-不符合审计原则
    isChangeApproval (state) {
      return state.detail.processDefinitionKey === "epc_change_approval";
    },
    // 是否是施工组织设计
    isOrganizeDesign (state) {
      return state.detail.processDefinitionKey === "epc_construction_build";
    },
    // 是否是施工组织设计
    isQualityInspection (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_construction_quality_inspection"
      );
    },
    isSafeInspection (state) {
      return state.detail.processDefinitionKey === "epc_construction_security";
    },
    isDataInspection (state) {
      return (
        state.detail.processDefinitionKey === "epc_construction_document_review"
      );
    },
    isClaim (state) {
      return state.detail.processDefinitionKey === "epc_construction_claim";
    },
    isCounterClaim (state) {
      return (
        state.detail.processDefinitionKey === "epc_construction_counter_claim"
      );
    },
    isVisa (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_construction_subpackaged_visa"
      );
    },
    isViolation (state) {
      return (
        state.detail.processDefinitionKey === "epc_construction_security_rule"
      );
    },
    isAccident (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_construction_security_accident"
      );
    },
    isTrailOperationPlan (state) {
      return (
        state.detail.processDefinitionKey === "epc_closing_trial_operation_plan"
      );
    },
    isPerformAssess (state) {
      return (
        state.detail.processDefinitionKey === "epc_closing_perform_assess_app"
      );
    },

    isMasterPlan (state) {
      return state.detail.processDefinitionKey === "epc_purchase_plan_master";
    },
    isPlanAdjust (state) {
      return state.detail.processDefinitionKey === "epc_purchase_plan_mod";
    },
    isPublicBidding (state) {
      return state.detail.processDefinitionKey === "epc_purchase_bid_public";
    },
    isInviteBidding (state) {
      return state.detail.processDefinitionKey === "epc_purchase_bid_invited";
    },
    isCompetitionArgue (state) {
      return state.detail.processDefinitionKey === "epc_purchase_bid_comp";
    },
    isPreBidding (state) {
      return state.detail.processDefinitionKey === "epc_purchase_pre_bid";
    },
    isNotPreBidding (state) {
      return state.detail.processDefinitionKey === "epc_purchase_no_pre_bid";
    },
    isInquiry (state) {
      return (
        state.detail.processDefinitionKey === "epc_purchase_main_contract_sign"
      );
    },
    isEmergancyPurchase (state) {
      return state.detail.processDefinitionKey === "epc_purchase_emergency";
    },
    isTransfer (state) {
      return state.detail.processDefinitionKey === "epc_purchase_transfer";
    },
    isReturnToFactory (state) {
      return state.detail.processDefinitionKey === "epc_purchase_return";
    },
    // 收支台账
    isBalanceAccount (state) {
      return state.detail.processDefinitionKey === "epc_balance_contract";
    },
    // 现场剩余物资处理
    isRemMatHandle (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_balance_remain_material_handling"
      );
    },
    // 竣工结算
    isComSettle (state) {
      return (
        state.detail.processDefinitionKey === "epc_balance_completion_settle"
      );
    },
    // 竣工结算审核
    isComSettleComment (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_balance_completion_settle_review"
      );
    },
    // 分包结算
    isSubSettle (state) {
      return state.detail.processDefinitionKey === "epc_balance_subpack_settle";
    },
    // 分包结算
    isSubSettleComment (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_balance_subpack_settle_review"
      );
    },
    // 成本执行报告
    isCosExeRep (state) {
      return (
        state.detail.processDefinitionKey === "epc_balance_cost_exec_report"
      );
    },
    // 成本执行报告
    isCosExeRepComment (state) {
      return (
        state.detail.processDefinitionKey ===
        "epc_balance_cost_exec_report_review"
      );
    },
    // 分包付款
    isSubpackPay (state) {
      return state.detail.processDefinitionKey === "epc_balance_subpack_pay";
    },
    // 分包支出申请
    isSubpackPayApp (state) {
      return (
        state.detail.processDefinitionKey === "epc_balance_subpack_expense_app"
      );
    },
    // 分包支付审批（总院）- 根据 paymentType 判断
    isSubpackagePaymentMainInstitute (state) {
      if (state.detail.processDefinitionKey !== "finance_payment_subcontract") {
        return false;
      }
      // 解析 extra 数据
      let extra = state.detail.extra;
      if (typeof extra === 'string') {
        try {
          extra = JSON.parse(extra);
        } catch (e) {
          return false;
        }
      }
      // paymentType 为 prod_institute 或未设置时，默认为总院
      return !extra || extra.paymentType === 'prod_institute' || !extra.paymentType;
    },
    // 分包支付审批（分院）- 根据 paymentType 判断
    isSubpackagePaymentBranchInstitute (state) {
      if (state.detail.processDefinitionKey !== "finance_payment_subcontract") {
        return false;
      }
      // 解析 extra 数据
      let extra = state.detail.extra;
      if (typeof extra === 'string') {
        try {
          extra = JSON.parse(extra);
        } catch (e) {
          return false;
        }
      }
      // paymentType 为 branch_institute 时，为分院
      return extra && extra.paymentType === 'branch_institute';
    },

    isBiddingRegister (state) {
      return state.detail.processDefinitionKey === "mark_bid_register";
    },

    isAddBidding (state) {
      return state.detail.processDefinitionKey === "mark_bid_create";
    },
    isBiddingResult (state) {
      return state.detail.processDefinitionKey === "mark_bid_jury";
    },
    isBiddingConfirm (state) {
      return state.detail.processDefinitionKey === "mark_bid_win";
    },



    // 备案立项申请
    isPutOnFileRequest (state) {
      return state.detail.processDefinitionKey === "scrm_iremote_filingConvertProjectProcess";
    },
    // 项目投标申请(EPC-非牵头人)
    isBidRequestEPCLeaderNot (state) {
      return state.detail.processDefinitionKey === "scrm_iremote_projectBidApplyEpcNotHeadProcess" || state.detail.processDefinitionKey === "scrm_iremote_projectStartMeetingProcess"  || state.detail.processDefinitionKey === "scrm_iremote_projectDesignInputProcess"   || state.detail.processDefinitionKey === "scrm_iremote_projectTechPlanPreReviewProcess";
    },
    // 项目投标申请(EPC-牵头人)
    isBidRequestEPCLeader (state) {
      return state.detail.processDefinitionKey === "scrm_iremote_projectBidApplyEpcHeadProcess";
    },
    // 项目投标申请(设计)
    isBidRequestDesign (state) {
      return state.detail.processDefinitionKey === "scrm_iremote_projectBidApplyDesignProcess";
    },


    // 是否是调休，加班，请假，出差和公出审批
    isLeave (state) {
      const arr = [ 'att_leave', 'att_on_business', 'att_adjust' ]
      return arr.includes(state.detail.processDefinitionKey)
    },
    // 是否是补卡审批
    isReplace (state) {
      return state.detail.processDefinitionKey === "att_check_apply";
    },

    // 是否是技术质量-安全检查-策划表
    ofc_quality_plan (state) {
      return state.detail.processDefinitionKey === "ofc_quality_plan";
    },
    // 是否是技术质量-安全检查-工作表
    ofc_quality_work (state) {
      return state.detail.processDefinitionKey === "ofc_quality_work";
    },
    // 是否是技术质量-安全检查-整改工作单
    ofc_quality_rectify (state) {
      return state.detail.processDefinitionKey === "ofc_quality_rectify";
    },


    // 是否是整改人审批
    isChargeOfRectification (state) {
      return state.detail.taskDefinitionKey === "ofc_quality_rectifier";
    },


    // 是否是部门协作单
    safety_collaborate (state) {
      return state.detail.processDefinitionKey === "safety_collaborate";
    },


    // 是否是受检项目人员审批
    ofc_quality_project_member (state) {
      return (
        state.detail.taskDefinitionKey === "ofc_quality_project_member"
      );
    },

    //是否是餐卡借用审批
    dine_card_temp (state) {
      return state.detail.processDefinitionKey === "dine_card_temp";
    },

    ofc_quality_fee_budget (state) {
      return state.detail.processDefinitionKey === "ofc_quality_fee_budget";
    },

    ofc_quality_fee_use (state) {
      return state.detail.processDefinitionKey === "ofc_quality_fee_use";
    },

    ofc_quality_risk (state) {
      return state.detail.processDefinitionKey === "ofc_quality_risk";
    },
    ofc_quality_meeting_issue (state) {
      return state.detail.processDefinitionKey === "ofc_quality_meeting_issue";
    },
    ofc_quality_meeting_decision (state) {
      return state.detail.processDefinitionKey === "ofc_quality_meeting_decision";
    },
    oa_use_car (state) {
      return state.detail.processDefinitionKey === "oa_use_car";
    },
    oa_ca_filing (state) {
      return state.detail.processDefinitionKey === "oa_ca_filing";
    },
    oa_technical_information_grant (state) {
      return state.detail.processDefinitionKey === "oa_technical_information_grant";
    },
    user_intern (state) {
      return state.detail.processDefinitionKey === "user_intern";
    },
    scrm_exclusive_area (state) {
      return state.detail.processDefinitionKey === "scrm_exclusive_area";
    },
    scrm_channel (state) {
      return state.detail.processDefinitionKey === "scrm_channel";
    },
    oa_it_asset_transfer_dept (state) {
      return state.detail.processDefinitionKey === "oa_it_asset_transfer_dept";
    },

    isReview (state) {
      return state.detail.processDefinitionKey === "cosys_design_review";
    },
    isAudit (state) {
      return state.detail.processDefinitionKey === "cosys_design_audit";
    },
    isCosysPrjReview (state) {
      return state.detail.processDefinitionKey === "cosys_prj_review";
    },
    isCosysPrjTaskNotice (state) {
      return state.detail.processDefinitionKey === "cosys_prj_task_notice";
    },
    isCosysDesignPlanRevise (state) {
      return state.detail.processDefinitionKey === "cosys_design_plan_revise";
    },
    isCosysDesignPlanSimple (state) {
      return state.detail.processDefinitionKey === "cosys_design_plan_simple";
    },
    isCosysStampReview (state) {
      return state.detail.processDefinitionKey === "cosys_stamp_review";
    },
    isCosysDesignArchive (state) {
      return state.detail.processDefinitionKey === "cosys_design_archive";
    },
    isCosysDesignCountersign (state) {
      return state.detail.processDefinitionKey === "cosys_design_countersign";
    },
    isCosysStampPrint (state) {
      return state.detail.processDefinitionKey === "cosys_stamp_print";
    },
    isCosysDesignInputReq (state) {
      return state.detail.processDefinitionKey === "cosys_design_input_req";
    },
    isCosysDesignPlan (state) {
      return state.detail.processDefinitionKey === "cosys_design_plan";
    },
    isCosysDesignChange (state) {
      return state.detail.processDefinitionKey === "cosys_design_change";
    },
    isCosysMajorTransfer (state) {
      return state.detail.processDefinitionKey === "cosys_major_transfer";
    },
    isCosysDesignAffirm (state) {
      return state.detail.processDefinitionKey === "cosys_design_affirm";
    },
    isCosysProjectFiling (state) {
      return state.detail.processDefinitionKey === "crm_project_filing" || state.detail.processDefinitionKey === "cosys_project_filing";
    },
    isCosysProjectBidApply (state) {
      return state.detail.processDefinitionKey === "crm_project_bidding" || state.detail.processDefinitionKey === "cosys_project_bidding";
    },
    isOaLeave (state) {
      return state.detail.processDefinitionKey === "oa_leave_apply" ;
    },
    isHrBranchEmployee (state) {
      return state.detail.processDefinitionKey === "user_branch_employee_register" ;
    },
    isPurchaseMaterials (state) {
      return state.detail.processDefinitionKey === "oa_purchase_materials_office" ;
    },
    isOaWorkOrder (state) {
      return state.detail.processDefinitionKey === "oa_work_order" ;
    },
    isCosysDesignReview (state) {
      return state.detail.processDefinitionKey === "cosys_design_review";
    },
    isCosysDesignService (state) {
      return state.detail.processDefinitionKey === "cosys_design_service";
    },
    isCosysCompletionReport (state) {
      return state.detail.processDefinitionKey === "cosys_completion_report";
    },
    isCosysBlueprintReplace (state) {
      return state.detail.processDefinitionKey === "cosys_blueprint_replace";
    },


  },

  mutations: {
    setTaskId (state, payload) {
      state.taskId = payload;
    },
    setInstanceId (state, payload) {
      state.instanceId = payload;
    },
    setLoading (state, payload) {
      state.loading = payload;
    },
    setDetail (state, payload) {
      state.detail = payload;
      // 切换任务时重置工单处理记录
      state.workOrderRecord = {
        problemType: "demand",
        modifyContent: "",
        files: [],
        isCharge: false,
      };
    },
    setWorkOrderRecord (state, payload) {
      state.workOrderRecord = { ...state.workOrderRecord, ...payload };
    },
    setHistory (state, payload) {
      state.history = payload;
    },

    setLog (state, payload) {
      state.log = payload;
    },

    // 打分
    setScore (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          phaseProgressData: {
            ...state.detail.extra.phaseProgressData,
            score: payload,
          },
        },
      };
    },
    setFormula (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          phaseProgressData: {
            ...state.detail.extra.phaseProgressData,
            formula: payload,
          },
        },
      };
    },
    setFactor (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          phaseProgressData: {
            ...state.detail.extra.phaseProgressData,
            factor: payload,
          },
        },
      };
    },

    // 设置考核年份
    setAssessmentYear (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            assessmentYear: payload,
          },
        },
      };
    },
    // 设置承接方式
    setUndertakingMode (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            undertakingMode: payload,
          },
        },
      };
    },
    // 设置经营组ID
    setBusinessGroupId (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            businessGroupId: payload,
          },
        },
      };
    },
    // 设置经营组名称
    setBusinessGroup (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            businessGroup: payload,
          },
        },
      };
    },
    // 设置经营对接人ID
    setBusinessContactUserId (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            businessContactUserId: payload,
          },
        },
      };
    },
    // 设置经营对接人名称
    setBusinessContactUserName (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractData: {
            ...state.detail.extra.contractData,
            businessContactUserName: payload,
          },
        },
      };
    },

    // 填写督办完成情况
    setExecuteRemarks (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          superviseTaskData: {
            ...state.detail.extra.superviseTaskData,
            executeRemarks: payload,
          },
        },
      };
    },

    // 上传督办完成情况附件
    setExecuteAttachments (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          superviseTaskData: {
            ...state.detail.extra.superviseTaskData,
            executeAttachments: [
              ...(Array.isArray(
                state.detail.extra.superviseTaskData.executeAttachments
              )
                ? state.detail.extra.superviseTaskData.executeAttachments
                : []),
              ...payload,
            ],
          },
        },
      };
    },

    // 设置是否有延期字段
    setIsDelayed (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          superviseTaskData: {
            ...state.detail.extra.superviseTaskData,
            isDelayed: payload,
          },
        },
      };
    },

    setOldDelayedDate (state, payload) {
      state.oldDelayedDate = payload;
    },

    setDelayedDate (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          superviseTaskData: {
            ...state.detail.extra.superviseTaskData,
            delayedDate: payload,
          },
        },
      };
    },

    setDelayRemarks (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          superviseTaskData: {
            ...state.detail.extra.superviseTaskData,
            delayRemarks: payload,
          },
        },
      };
    },

    // 财务开票
    // 填写销方
    addSale (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          invoiceInfos: state.detail.extra.invoiceInfos
            ? {
              ...state.detail.extra.invoiceInfos,
              salesUnitId: payload.id,
              salesUnitName: payload.name,
            }
            : {
              salesUnitId: payload.id,
              salesUnitName: payload.name,
            },
        },
      };
    },

    // 填写开票人
    addDrawer (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,

          invoiceInfos: state.detail.extra.invoiceInfos
            ? {
              ...state.detail.extra.invoiceInfos,
              drawer: payload,
            }
            : {
              drawer: payload,
            },
        },
      };
    },
    // 填写开票时间
    addDrawDate (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          invoiceInfos: state.detail.extra.invoiceInfos
            ? {
              ...state.detail.extra.invoiceInfos,
              drawDate: payload,
            }
            : {
              drawDate: payload,
            },
        },
      };
    },

    // 添加新的发票
    addInvoice (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,

          invoiceInfos: state.detail.extra.invoiceInfos
            ? {
              ...state.detail.extra.invoiceInfos,
              invoiceList: payload,
            }
            : {
              invoiceList: payload,
            },
        },
      };
    },

    // 填写送达确认信息
    setExpress (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          sendInfo: state.detail.extra.sendInfo
            ? {
              ...state.detail.extra.sendInfo,
              ...payload,
            }
            : {
              ...payload,
            },
        },
      };
    },

    // 加签
    setSignUser (state, payload) {
      state.assignees = payload;
    },
    setSignType (state, payload) {
      state.signType = payload;
    },
    setOnSet (state, payload) {
      state.onset = payload;
    },

    // 设置是否需要财务审批
    setIsFinancialApproval (state, payload) {
      state.detail = {
        ...state.detail,
        extra: {
          ...state.detail.extra,
          contractChangeAmountData: {
            ...state.detail.extra.contractChangeAmountData,
            isFinancialApproval: payload,
          },
        },
      };
    },

    // 设置校审意见列表
    setFilteredOpinionList (state, payload) {
      state.filteredOpinionList = payload;
    },

  },

  actions: {
    getDetail ({ state, commit }) {
      return new Promise((resolve) => {
        commit("setLoading", true);
        const requestTaskId = state.taskId;
        const requestInstanceId = state.instanceId;
        fetchTaskDetail({
          taskId: requestTaskId,
          instanceId: requestInstanceId,
        })
          .then((res) => {
            if (state.taskId !== requestTaskId || state.instanceId !== requestInstanceId) {
              resolve();
              return;
            }
            if (res && Array.isArray(res.proTasks)) {
              commit("setHistory", res.proTasks);
            }
            if (res && res.currentTask) {
              // extra 解析统一走 safeParseExtra：字符串解析失败时有修复逻辑（attachment 未转义引号等）并输出诊断日志
              // 旧的内联 try/catch 解析失败会静默置空 {}，导致表单数据全部不显示，且 safeParseExtra 的修复永远不生效
              // if (typeof res.currentTask.extra === "string") {
              //   try {
              //     extra = JSON.parse(res.currentTask.extra);
              //   } catch (e) {
              //     extra = {};
              //   }
              // } else if (res.currentTask.extra && typeof res.currentTask.extra === "object") {
              //   extra = res.currentTask.extra;
              // }
              const extra = safeParseExtra(res.currentTask.extra);
              if (!extra.seqNum && res.currentTask.seqNum) {
                extra.seqNum = res.currentTask.seqNum;
              }
              commit("setDetail", {
                ...res.currentTask,
                extra: safeParseExtra(extra),
              });

              if (extra.superviseTaskData) {
                commit(
                  "setOldDelayedDate",
                  extra.superviseTaskData.delayedDate
                );
              }
            }
            resolve();
          })
          .catch(() => {
            resolve();
          })
          .finally(() => {
            commit("setLoading", false);
          });
      });
    },

    getLog ({ state, commit }) {
      fetchLog({
        instanceId: state.instanceId,
      }).then((res) => {
        if (res) {
          commit("setLog", res);
        }
      });
    },

    finishTask ({ state }, payload) {
      console.log(payload);
      console.log(state);
      console.log(state.taskDefinitionKey);

      let extraDecoded = payload.extra;
      if (typeof extraDecoded === 'string') {
        try {
          const parsed = JSON.parse(extraDecoded);
          decodeExtraUrls(parsed);
          extraDecoded = JSON.stringify(parsed);
        } catch (e) {}
      } else if (extraDecoded && typeof extraDecoded === 'object') {
        decodeExtraUrls(extraDecoded);
        extraDecoded = JSON.stringify(extraDecoded);
      }

      return new Promise((resolve, reject) => {
        done({
          taskId: state.taskId,
          taskDefinitionKey: state.taskDefinitionKey,
          ...payload,
          extra: extraDecoded,
        })
          .then(() => {
            resolve();
          })
          .catch(reject);
      });
    },

    storageTask ({ state }, payload) {
      return new Promise((resolve, reject) => {
        storage({
          taskId: state.taskId,
          ...payload,
        })
          .then(() => {
            resolve();
          })
          .catch(reject);
      });
    },

    // 加签查询
    getSign ({ state }) {
      return new Promise((resolve, reject) => {
        fetchSign({
          taskId: state.taskId,
          processDefId: state.detail.processDefId,
          processInstanceId: state.instanceId,
          taskDefkey: state.detail.taskDefinitionKey,
        })
          .then((res) => {
            resolve(res);
          })
          .catch(reject);
      });
    },
    // 加签暂存
    signTaskStorage ({ state }) {
      return new Promise((resolve, reject) => {
        signStorage({
          taskId: state.taskId,
          signType: state.signType,
          assignees: state.assignees.map((item) => item.userId),
          onSet: state.onset,
        })
          .then(() => {
            resolve();
          })
          .catch(reject);
      });
    },

    // 加签提交
    signTaskSubmit ({ state }, payload) {
      console.log(payload);
      return new Promise((resolve, reject) => {
        signSubmit({
          taskId: state.taskId,
          signType: state.signType,
          assignees: state.assignees.map((item) => item.userId),
          onSet: state.onset,
          map: payload,
        })
          .then(() => {
            resolve();
          })
          .catch(reject);
      });
    },
  },
};
